<template>
  <Action :action="mappedAction" :completed="completed" @open-edit-component="show">
    <!-- eslint-disable-next-line vue/singleline-html-element-content-newline -->
    <div class="font-bold">
      <table>
        <tbody>
          <tr>
            <td class="color white-space-nowrap">
              {{ t("components.issueModal.technicalPlace") }}
            </td>
            <td class="pl-3">
              {{ action.technicalPlace?.path + " | " + action.technicalPlace?.name }}
            </td>
          </tr>

          <tr v-if="action.repTime && action.repTime.length > 0">
            <td class="color white-space-nowrap">
              {{ t("components.issueModal.repTime") }}
            </td>
            <td class="pl-3">
              {{ formatDate(action.repTime[0]) }} <i class="fa fa-arrow-right fa-xs" />
              {{ formatDate(action.repTime[1]) }}
            </td>
          </tr>

          <tr>
            <td class="color white-space-nowrap">{{ t("components.issueModal.plannedTime") }}</td>
            <td class="pl-3">{{ action.plannedTime + " " + t("license.ih.ih.hour") }}</td>
          </tr>

          <tr>
            <td class="color white-space-nowrap">{{ t("components.issueModal.description") }}</td>
            <td class="pl-3 white-space-pre">{{ action.description }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <template #headerAdditions>
      <AttachmentBadge :files="action.Folder?.files" class="inline" />
    </template>
    <template #oneLineContent>
      {{ oneLineContent }}
    </template>
    <template #assignee>
      <div class="assignee">
        <UserBadge
          v-if="action.claimed"
          :user="action.assignee"
          class="bounceTag"
          @click="setUserFilter(action.assignee, $event)"
        />
        <span v-else class="px-2 text-error">{{ t("license.ih.ih.hideResponsibleModal") }}</span>
      </div>
    </template>
    <IHOrderModalWrapper
      v-if="workflowModalVisible"
      v-model:visible="workflowModalVisible"
      :action="mappedAction"
      :event="event"
    />
  </Action>
</template>

<script lang="ts">
import { useTranslation } from "@/language/translation";

import { computed, defineComponent, type PropType, ref, provide } from "vue";
import { DateTime } from "luxon";
import {
  type ActionListActionFields_IHOrder_Fragment,
  type EventListEventFieldsFragment,
  TaskStatus,
} from "@/graphql";
import Action from "@/components/event-action/action/ActionComponent.vue";
import IHOrderModalWrapper from "./IHOrderModalWrapper.vue";
import { LISA_DATE_SHORT } from "@/language/translation";
import getBadgeColor from "@/functions/badgeColor";
import UserBadge from "@/components/badges/UserBadge.vue";
import { CommentUserIdInjectionKey } from "@/views/event-action/EventActionBoardInjectionKeys";
import AttachmentBadge from "@/components/badges/AttachmentBadge.vue";
import { CreatorFilter } from "@/components/event-action/filter/filterImplementations";
import injectAsserted from "@/functions/assert/injectAsserted";
import { ActionFilterInjectionKey } from "@/components/event-action/filter/FilterTypes";

export default defineComponent({
  name: "IHOrderAction",
  components: {
    Action,
    IHOrderModalWrapper,
    UserBadge,
    AttachmentBadge,
  },
  props: {
    action: {
      type: Object as PropType<ActionListActionFields_IHOrder_Fragment>,
      required: true,
    },
    event: {
      type: Object as PropType<
        EventListEventFieldsFragment & {
          technicalCategoryId: number | undefined;
          technicalPlaceId: number | undefined;
        }
      >,
      required: true,
    },
  },
  setup(props) {
    const { t } = useTranslation();
    const workflowModalVisible = ref(false);
    const mappedAction = computed(() => ({
      ...props.action,
    }));
    const oneLineContent = computed(() => {
      return props.action.description;
    });

    const show = () => {
      workflowModalVisible.value = true;
    };
    const actionFilters = injectAsserted(ActionFilterInjectionKey);

    const formatDate = (value: string) => {
      return DateTime.fromISO(value).toLocaleString(LISA_DATE_SHORT);
    };

    const completed = computed(() => {
      return props.action.status === TaskStatus.act;
    });
    function setUserFilter(owner: EventListEventFieldsFragment["owner"], evt: MouseEvent) {
      if ((evt.ctrlKey || evt.metaKey) && owner != null) {
        actionFilters.value = {
          updatedAt: DateTime.now(),
          actionFilters: [new CreatorFilter([owner?.id])],
        };
      }
    }
    const color = computed(() => {
      return getBadgeColor(props.action.status, props.action.repTime[1]);
    });

    const commentUserId = ref<number>();
    provide(CommentUserIdInjectionKey, commentUserId);

    return {
      t,
      workflowModalVisible,
      mappedAction,
      oneLineContent,
      completed,
      color,
      LISA_DATE_SHORT,
      setUserFilter,
      // methods
      show,
      formatDate,
    };
  },
});
</script>

<style lang="scss" scoped>
.color {
  color: #166b95;
}

table td {
  vertical-align: top;
}
</style>
